import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

interface HeroSlide {
  id: string;
  title: string;
  tagline: string;
  description: string;
  tags: string[];
  rating: string;
  image: string;
}

const SLIDES: HeroSlide[] = [
  {
    id: 'mv-01',
    title: '星海远征',
    tagline: '年度科幻巨制 · 震撼上映',
    description: '穿越虫洞，寻找人类最后的家园。一场关于时间、亲情与勇气的星海远征。',
    tags: ['科幻', '冒险', '高分'],
    rating: '8.7',
    image: 'https://readdy.ai/api/search-image?query=Epic%20sci-fi%20space%20scene%2C%20astronaut%20floating%20before%20a%20vast%20glowing%20nebula%20and%20rings%20of%20planets%2C%20deep%20blue%20and%20warm%20orange%20contrast%2C%20cinematic%20wide%20dramatic%20lighting&width=1600&height=900&seq=hero-01&orientation=landscape',
  },
  {
    id: 'tv-01',
    title: '长安秘事',
    tagline: '古装悬疑 · 全网热播',
    description: '盛唐长安，离奇命案接连发生。年轻司直与神秘画师携手，揭开撼动朝局的惊天秘密。',
    tags: ['古装', '悬疑', '权谋'],
    rating: '8.8',
    image: 'https://readdy.ai/api/search-image?query=Ancient%20Chinese%20Tang%20dynasty%20cityscape%20at%20night%20with%20thousands%20of%20glowing%20lanterns%20and%20pagodas%2C%20mysterious%20deep%20blue%20and%20gold%20tones%2C%20cinematic%20epic%20historical%20scene&width=1600&height=900&seq=hero-02&orientation=landscape',
  },
  {
    id: 'mv-04',
    title: '峰顶之巅',
    tagline: '动作冒险 · 实景拍摄',
    description: '在世界最难峰顶遭遇暴风雪，队友之间必须彼此信任，才能活着走出雪山。',
    tags: ['动作', '冒险', '震撼'],
    rating: '8.4',
    image: 'https://readdy.ai/api/search-image?query=Dramatic%20snowy%20mountain%20peak%20with%20climber%20silhouette%20and%20swirling%20snow%2C%20cold%20deep%20blue%20sky%20and%20warm%20sunlight%20edge%2C%20epic%20cinematic%20scale&width=1600&height=900&seq=hero-03&orientation=landscape',
  },
  {
    id: 'tv-03',
    title: '未来边界',
    tagline: '科幻悬疑 · 高分口碑',
    description: '人工智能统治的未来世界，一群反抗者试图突破城市边界，寻找传说中的自由之地。',
    tags: ['科幻', '烧脑', '高分'],
    rating: '8.9',
    image: 'https://readdy.ai/api/search-image?query=Futuristic%20cyberpunk%20megacity%20skyline%20at%20dusk%20with%20holographic%20towers%20and%20flying%20vehicles%2C%20electric%20teal%20and%20orange%20neon%20reflections%2C%20cinematic%20wide&width=1600&height=900&seq=hero-04&orientation=landscape',
  },
];

export default function HeroCarousel() {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % SLIDES.length);
    }, 5500);
    return () => clearInterval(timer);
  }, []);

  const goTo = (index: number) => setCurrent((index + SLIDES.length) % SLIDES.length);

  const slide = SLIDES[current];

  return (
    <section className="relative w-full h-[460px] overflow-hidden bg-primary-950 md:h-[600px]">
      {SLIDES.map((item, index) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={item.image}
            alt={`${item.title}剧照`}
            title={`${item.title} - 八戒影院推荐影视`}
            className="h-full w-full object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-950/70 to-primary-950/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-transparent to-primary-950/30" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-end px-4 pb-14 md:px-6 md:pb-20">
        <div className="max-w-2xl">
          <h1 className="text-2xl font-black tracking-wide text-background-50 font-heading md:text-3xl">八戒影院</h1>
          <p className="mt-1 text-xs tracking-[0.35em] text-secondary-400 md:text-sm">丰富电影电视剧在线观看平台</p>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 text-xs font-semibold text-background-50">
            <i className="ri-fire-line" />
            {slide.tagline}
          </span>
          <h2 className="mt-3 text-4xl font-black leading-tight text-background-50 font-heading md:text-6xl">
            {slide.title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="flex items-center gap-1 rounded-md bg-secondary-500 px-2 py-0.5 text-sm font-bold text-primary-950">
              <i className="ri-star-fill" />
              {slide.rating}
            </span>
            {slide.tags.map((tag) => (
              <span key={tag} className="rounded-md bg-primary-900/80 px-2 py-0.5 text-xs text-background-200">
                {tag}
              </span>
            ))}
          </div>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {slide.description}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${slide.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              <i className="ri-play-fill text-lg" />
              立即观看
            </Link>
            <Link
              to={`/detail/${slide.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50/15 px-6 py-2.5 text-sm font-semibold text-background-50 backdrop-blur transition-colors hover:bg-background-50/25"
            >
              <i className="ri-information-line text-lg" />
              查看详情
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(current - 1)}
        className="absolute left-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-primary-950/80 md:left-6 md:h-11 md:w-11"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-2xl" />
      </button>
      <button
        type="button"
        onClick={() => goTo(current + 1)}
        className="absolute right-3 top-1/2 z-20 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-primary-950/50 text-background-50 transition-colors hover:bg-primary-950/80 md:right-6 md:h-11 md:w-11"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-2xl" />
      </button>

      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2">
        {SLIDES.map((item, index) => (
          <button
            key={item.id}
            type="button"
            onClick={() => goTo(index)}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              index === current ? 'w-8 bg-accent-500' : 'w-3 bg-background-50/50'
            }`}
            aria-label={`切换到${item.title}`}
          />
        ))}
      </div>
    </section>
  );
}